import { useState, useCallback } from 'react'
import { supabase } from '../services/supabase'
import { normalizarPeriodicidade } from '../utils/periodicidadePix'

export interface CriarAssinaturaCartaoParams {
  valor: number
  descricao: string
  periodicidade?: string
  payment_token: string
  card_mask?: string
  planoId?: string
  customer: {
    name: string
    email: string
    cpf: string
    birth?: string
    phone_number: string
  }
  billing_address: {
    street: string
    number: string
    neighborhood: string
    zipcode: string
    city: string
    state: string
  }
}

export interface AssinaturaCartaoResponse {
  ok: boolean
  provider: string
  metodo: string
  periodicidade: string
  intervalMeses: number
  planId: number
  subscriptionId: number | null
  chargeId: number | null
  status: string | null
  chargeStatus?: string | null
  subscriptionStatus?: string | null
  classificacao?: 'confirmado' | 'recusado' | 'aguardando'
  refuseReason?: string | null
  total: number
  cardMask?: string | null
  raw?: unknown
}

export interface LinkAssinaturaCartaoResponse {
  ok: boolean
  provider: string
  metodo: string
  periodicidade: string
  intervalMeses: number
  planId: number
  subscriptionId: number | null
  chargeId: number | null
  paymentUrl: string
  status: string | null
  chargeStatus?: string | null
  subscriptionStatus?: string | null
  classificacao?: 'confirmado' | 'recusado' | 'aguardando'
  total: number
  webhookConfigured?: boolean
  raw?: unknown
}

export interface StatusCobrancaCartaoResponse {
  ok: boolean
  status: string | null
  chargeStatus?: string | null
  subscriptionStatus?: string | null
  classificacao: 'confirmado' | 'recusado' | 'aguardando'
  confirmado: boolean
  recusado: boolean
  aguardando: boolean
  refuseReason?: string | null
  chargeId?: number | null
  subscriptionId?: number | null
}

function backendUrl() {
  return (
    import.meta.env.VITE_BACKEND_PIX_AUTO_URL ||
    import.meta.env.VITE_BACKEND_PIX_URL ||
    'http://localhost:3005'
  )
}

async function authHeaders() {
  const { data: { session } } = await supabase.auth.getSession()
  return {
    'Content-Type': 'application/json',
    ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
  }
}

export function useEfiCartao() {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const criarAssinaturaCartao = async (
    params: CriarAssinaturaCartaoParams
  ): Promise<AssinaturaCartaoResponse | null> => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch(`${backendUrl()}/api/pix-auto/cartao/assinaturas`, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({
          ...params,
          periodicidade: normalizarPeriodicidade(params.periodicidade),
          custom_id: params.planoId,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        const desc = data?.details?.error_description
        const descText =
          typeof desc === 'string'
            ? desc
            : desc && typeof desc === 'object'
              ? `${desc.property ? String(desc.property).replace(/^\//, '') + ': ' : ''}${desc.message || ''}`.trim()
              : ''
        const message =
          (typeof data.error === 'string' && data.error) ||
          descText ||
          data?.message ||
          'Erro ao criar assinatura no cartao'
        setError(message)
        return null
      }

      return data as AssinaturaCartaoResponse
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Erro ao comunicar com backend EFI'
      setError(message)
      return null
    } finally {
      setLoading(false)
    }
  }

  const consultarStatusCartao = useCallback(
    async (params: {
      chargeId?: number | null
      subscriptionId?: number | null
    }): Promise<StatusCobrancaCartaoResponse | null> => {
      try {
        const qs = new URLSearchParams()
        if (params.chargeId) qs.set('chargeId', String(params.chargeId))
        if (params.subscriptionId) qs.set('subscriptionId', String(params.subscriptionId))

        const response = await fetch(
          `${backendUrl()}/api/pix-auto/cartao/status?${qs.toString()}`,
          { headers: await authHeaders() }
        )
        const data = await response.json()
        if (!response.ok) {
          setError(data?.error || 'Erro ao consultar status do cartão')
          return null
        }
        return data as StatusCobrancaCartaoResponse
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Erro ao consultar status do cartão'
        setError(message)
        return null
      }
    },
    []
  )

  const criarLinkAssinaturaCartao = useCallback(
    async (params: {
      valor: number
      descricao: string
      periodicidade?: string
      planoId?: string
      assinaturaId?: string
    }): Promise<LinkAssinaturaCartaoResponse | null> => {
      setLoading(true)
      setError(null)

      try {
        const customId = params.assinaturaId || params.planoId
        const response = await fetch(`${backendUrl()}/api/pix-auto/cartao/link`, {
          method: 'POST',
          headers: await authHeaders(),
          body: JSON.stringify({
            ...params,
            periodicidade: normalizarPeriodicidade(params.periodicidade),
            custom_id: customId,
            assinaturaId: params.assinaturaId,
          }),
        })

        const data = await response.json()

        if (!response.ok) {
          const message =
            (typeof data.error === 'string' && data.error) ||
            data?.message ||
            'Erro ao gerar link de pagamento EFI'
          setError(message)
          return null
        }

        return data as LinkAssinaturaCartaoResponse
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Erro ao comunicar com backend EFI'
        setError(message)
        return null
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const confirmarPagamentoCartao = useCallback(
    async (params: {
      chargeId?: number | null
      subscriptionId?: number | null
      planoId?: string | null
      assinaturaId?: string | null
    }): Promise<{
      ok: boolean
      ativado: boolean
      confirmado?: boolean
      classificacao?: string
      status?: string | null
      refuseReason?: string | null
    } | null> => {
      try {
        const response = await fetch(`${backendUrl()}/api/pix-auto/cartao/confirmar`, {
          method: 'POST',
          headers: await authHeaders(),
          body: JSON.stringify({
            chargeId: params.chargeId,
            subscriptionId: params.subscriptionId,
            planoId: params.planoId,
            assinaturaId: params.assinaturaId,
          }),
        })
        const data = await response.json()
        if (!response.ok) {
          setError(data?.error || 'Erro ao confirmar pagamento do cartão')
          return null
        }
        return data
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Erro ao confirmar pagamento do cartão'
        setError(message)
        return null
      }
    },
    []
  )

  return {
    loading,
    error,
    criarAssinaturaCartao,
    criarLinkAssinaturaCartao,
    consultarStatusCartao,
    confirmarPagamentoCartao,
  }
}
